/**
 * Server-side / API contract types mirrored from the OASIS backend
 * (`src/_shared/oasis/types.ts` in the API repo). The applicant-facing
 * body shape (`IOasisApplicationBase`) lives in `oasis-application.types.ts`.
 *
 * Keep these in sync with the API repo manually — they're flat enums and
 * a handful of interfaces, so drift is easy to catch in PR review.
 *
 * The workflow is two-level (see the API's `docs/oasis-cora-workflow-redesign.md`):
 * an application spine (`OasisApplicationStatus`) plus per-component review lanes
 * (`ComponentReviewStatus`).
 */

import type {
	IOasisApplicationBase,
	OarmReviewFlag,
	OasisDocType,
	ProgramType,
	SlipTrack,
	VeteranPreferenceCategory,
} from './oasis-application.types';

export type { OasisDocType };

// ─── Application-level status (the OARM spine) ───────────────────────────────

/** Server-controlled top-line lifecycle status for an application. */
export type OasisApplicationStatus =
	| 'in_progress'
	| 'submitted'
	| 'flag_review'
	| 'returned'
	| 'resubmitted'
	| 'ineligible'
	| 'component_review'
	| 'ineligible_for_interview'
	| 'ineligible_for_offer'
	| 'offer_accepted'
	| 'hired'
	| 'not_selected'
	| 'withdrawn';

/**
 * Application statuses that are terminal.
 *
 * Mirrors the API's list. Only `hired` remains: withdrawal, the two clearance denials,
 * `ineligible` and `not_selected` all became reversible, each with a `component_review`
 * or prior-status edge back. Keep the two in step.
 */
export const TERMINAL_APP_STATUSES: readonly OasisApplicationStatus[] = ['hired'];

// ─── Component-level status (per selected component, parallel) ───────────────

/** Status of a single component's review lane. */
export type ComponentReviewStatus =
	| 'in_queue'
	| 'selected_for_interview' // component wants an interview; awaiting OARM clearance
	| 'cleared_for_interview'
	| 'not_cleared_for_interview' // OARM refused; reversible, so NOT terminal
	| 'interview_requested'
	| 'interview_accepted'
	| 'interview_declined'
	| 'interview_scheduled'
	| 'interviewed'
	| 'not_selected' // component passed on the candidate, at any stage
	| 'selected_for_offer' // component wants to offer; awaiting OARM clearance
	| 'cleared_for_offer'
	| 'not_cleared_for_offer' // OARM refused; reversible, so NOT terminal
	| 'offer_issued'
	| 'offer_accepted'
	| 'offer_declined'
	| 'offer_rescinded';

/**
 * Component-lane statuses that end that lane for good. The `not_cleared_*` statuses are
 * absent on purpose: OARM may reverse a clearance denial, so a denied lane is parked.
 */
export const TERMINAL_COMPONENT_STATUSES: readonly ComponentReviewStatus[] = ['interview_declined', 'not_selected', 'offer_declined'];

// ─── OARM clearances ─────────────────────────────────────────────────────────

/**
 * State of one OARM clearance decision. Stored on the header only for HP, where the
 * decision is about the applicant; for SLIP the lane status carries it per position.
 */
export type ClearanceState = 'pending' | 'cleared' | 'not_cleared';

/** Which OARM clearance is being decided. */
export type ClearanceGate = 'interview' | 'offer';

/**
 * What OARM submits. `undecided` withdraws a denial and returns the lane to the request,
 * so it re-enters OARM's worklist — for a decision waiting on outside information.
 */
export type ClearanceOutcome = 'cleared' | 'not_cleared' | 'undecided';

// ─── Workflow events ─────────────────────────────────────────────────────────

export type WorkflowEventType =
	| 'submitted'
	| 'resubmitted'
	| 'withdrawn'
	| 'flagged'
	| 'returned'
	| 'cleared'
	| 'ineligible'
	| 'component_transition'
	| 'interview_clearance'
	| 'offer_clearance'
	| 'offer_accepted'
	| 'offer_rescinded'
	| 'not_selected'
	| 'reviews_locked'
	| 'reviews_unlocked'
	| 'reopened'
	// NOTE this union has drifted from the API's `WorkflowEventType`, which also carries
	// `program_changed` and `answers_cleared`. Pre-existing; see TODO 44.
	| 'withdrawal_reversed'
	| 'not_selection_reversed'
	| 'note';

export interface WorkflowEvent {
	eventId: string;
	type: WorkflowEventType;
	occurredAt: string;
	actorUid: string;
	actorRole?: string;
	actorName?: string;
	actorEmail?: string;
	fromStatus?: OasisApplicationStatus;
	toStatus?: OasisApplicationStatus;
	componentCode?: string;
	componentFromStatus?: ComponentReviewStatus;
	componentToStatus?: ComponentReviewStatus;
	/**
	 * INTERNAL, staff-eyes-only commentary (OARM triage, component reviewer prep
	 * notes, gate rationale). The server drops this before it reaches an applicant,
	 * so on the applicant app it is always absent. Do not render it there.
	 */
	note?: string;
	/**
	 * A message written TO the applicant — the only free text that survives the
	 * applicant projection. This is what a returned application's "here is what to
	 * fix" arrives in. Safe to render in the applicant microsite.
	 */
	applicantMessage?: string;
	data?: Record<string, unknown>;
}

// ─── Application header ──────────────────────────────────────────────────────

export interface OasisApplicationHeader {
	applicationId: string;
	uid: string;
	cycleYear: number;
	program?: ProgramType;
	slipTrack?: SlipTrack;
	status: OasisApplicationStatus;
	/** Denormalized applicant display fields (copied from the body for list/queue views). */
	applicantName?: string;
	applicantEmail?: string;
	/** Denormalized review flags (OARM tags + display-only tags) for the flags column. */
	reviewFlags?: OarmReviewFlag[];
	/** Denormalized veterans' preference category (SSP/TP/XP/CP/CPS) when claimed. */
	veteransPreferenceCategory?: VeteranPreferenceCategory;
	/**
	 * The reviewer's message to the applicant about their CURRENT status — e.g. what
	 * to correct before resubmitting a returned application. Denormalized onto the
	 * header so the applicant dashboard can show it from the list call alone.
	 *
	 * Safe to render next to `status` without cross-checking dates: the server only
	 * writes one for the statuses where it applies (`returned`, `ineligible`), and the
	 * applicant API only SENDS it while it still applies (those two, plus
	 * `in_progress` while the applicant works the correction). It is filtered by
	 * audience rather than cleared, because OARM keeps their copy after the applicant
	 * resubmits — that is when they re-open the application to check the fix.
	 *
	 * Staff-authored and read-only to the applicant client.
	 */
	applicantMessage?: string;
	/** When {@link applicantMessage} was written (ISO 8601). */
	applicantMessageAt?: string;
	/**
	 * How many times OARM has returned this application. Absent or 0 means never
	 * returned.
	 *
	 * Server-owned and read-only to both clients. It drives submit-time routing (a
	 * previously-returned application always goes back to OARM rather than
	 * auto-clearing to component review), and the admin UI can show it so a
	 * reviewer knows this is not the applicant's first correction.
	 */
	returnCount?: number;
	/**
	 * When the applicant asked to move Honors Program → SLIP Law Clerk (ISO 8601).
	 *
	 * Server-owned and read-only to both clients. Its presence WITH `status:
	 * 'in_progress'` is what tells the UI a transfer edit session is open, which is how
	 * the applicant form knows to show the transfer banner instead of the ordinary
	 * "returned, please resubmit" reminder, and to freeze the program radios.
	 *
	 * Never cleared, so it is NOT on its own a sign the transfer is unfinished — a
	 * resubmitted transfer still carries it. Always read it together with `status`.
	 */
	programChangeRequestedAt?: string;
	createdAt: string;
	lastSavedAt: string;
	/**
	 * Set when the application first transitions to `submitted`. Absent while the
	 * application is still an `in_progress` draft (it has never been submitted).
	 */
	submittedAt?: string;
	resubmittedAt?: string;
	resubmissionEligible?: boolean;

	// ── OARM clearances (HP only; SLIP carries them on each lane) ──
	interviewClearance?: ClearanceState;
	/** uid of the OARM user who decided. */
	interviewClearanceBy?: string;
	interviewClearanceAt?: string;
	offerClearance?: ClearanceState;
	offerClearanceBy?: string;
	offerClearanceAt?: string;

	// ── Denormalized lane summary, for the staff landing grid ──
	/** Lanes waiting on an OARM interview clearance, and on an offer clearance. */
	pendingInterviewClearances?: number;
	pendingOfferClearances?: number;
	/** The applicant's components of interest, with each lane's status once review starts. */
	componentSelections?: { code: string; status?: ComponentReviewStatus }[];

	locked?: boolean;
}

// ─── Component review lane ───────────────────────────────────────────────────

export interface ComponentReview {
	applicationId: string;
	componentCode: string;
	/** Owning DOJ component (e.g. `CIV`), derived from the selection code. */
	dojComponent: string;
	componentLabel?: string;
	cycleYear: number;
	status: ComponentReviewStatus;
	createdAt: string;
	updatedAt: string;
}

// ─── Cycle config ────────────────────────────────────────────────────────────

/**
 * PUBLIC, UNAUTHENTICATED view of a cycle's configuration.
 *
 * Served to both front ends before anyone logs in, so the applicant portal can render
 * "applications open on…" and the published deadlines without compiling them into the
 * bundle.
 *
 * PUBLIC BY CONSTRUCTION — nothing staff-only may ever be added here. Review-flag
 * labels, internal thresholds, and anything OARM wants withheld from components all
 * belong on the authenticated application read instead. The test is simple: if an
 * anonymous visitor must not see it, it does not go in this type.
 *
 * Dates are for DISPLAY and progressive disclosure only. Authorization is always the
 * server's `flags.canEdit`, never a client-side comparison against these values.
 */
export interface OasisPublicConfig {
	cycleYear: number;
	label?: string;
	submissionOpens: string;
	/** Applications close here, and component review opens at the same instant. */
	submissionDeadline: string;
	/** End of the HP→SLIP-Law-Clerk window, which opens at `submissionDeadline`. */
	programChangeDeadline?: string;
	ombNumber: string;
	ombExpirationDate: string;
	/**
	 * RESOLVED J.D. recency cutoff — the per-cycle override when set, otherwise
	 * October 1 of (cycleYear − 4). Always present, deliberately: the client must
	 * never re-derive this. Deriving it locally is how the client's hard stop and the
	 * server's submit-time backstop silently disagree.
	 */
	jdEligibilityCutoff: string;
}

/**
 * Mirror of the server's `OasisCycle` (`_shared/oasis/types.ts`). Keep the two in
 * step — they are separate copies because the API cannot import this library.
 */
export interface OasisCycle {
	cycleYear: number;
	submissionOpens: string;
	/**
	 * When applications close, and by the same instant when component review opens.
	 * Also the moment the HP→SLIP-Law-Clerk window opens; that boundary is derived
	 * from this value rather than stored, so the two can never disagree.
	 */
	submissionDeadline: string;
	/** End of the HP→SLIP-Law-Clerk transfer window: `[submissionDeadline, this)`. */
	programChangeDeadline?: string;
	/** LEGACY — superseded by the transfer window. No writer sets `resubmissionEligible`. */
	resubmissionOpens?: string;
	/** LEGACY — superseded by the transfer window. */
	resubmissionDeadline?: string;
	ombNumber: string;
	ombExpirationDate: string;
	label?: string;
	/**
	 * Per-cycle override of the standing "October 1 of cycleYear − 4" J.D. recency
	 * rule. When set, it is authoritative — the client must not derive this
	 * threshold locally, or its hard stop will disagree with the server's backstop.
	 */
	jdEligibilityCutoff?: string;
}

// ─── Upload metadata ─────────────────────────────────────────────────────────

export interface OasisUploadRef {
	s3Key: string;
	docType: OasisDocType;
	filename: string;
	contentType: string;
	sizeBytes: number;
	uploadedAt: string;
}

export interface PresignedPutResponse {
	s3Key: string;
	uploadUrl: string;
	expiresAt: string;
	maxSizeBytes: number;
	/**
	 * Echoes the requested document's position in the `documents` array so a
	 * multi-file response can be correlated back to the request.
	 */
	index: number;
}

export interface PresignedGetResponse {
	s3Key: string;
	downloadUrl: string;
	expiresAt: string;
}

// ─── Composite read shape (GET /oasis-application/{id}) ──────────────────────

/**
 * What actions the reading caller may take, derived server-side.
 *
 * Mirrors `OasisAvailableActions` in the API's `_shared/oasis/types.ts`. Deliberately
 * a plain data shape with no logic: the whole point is that the client no longer
 * decides which transitions are legal. Do NOT add a client-side transition table
 * beside this — the server derives it from the tables, the caller's DB-resolved
 * scopes, and this application's gate flags, and only the first of those three is
 * knowable here.
 */
export interface OasisAvailableActions {
	/** Spine statuses the caller may move the application to. Empty for non-OARM. */
	appTransitions: OasisApplicationStatus[];
	/**
	 * The subset of `appTransitions` whose transition may carry a message to the
	 * applicant. Served because the server DISCARDS a message for any other status —
	 * do not infer this list here, or OARM gets a field that goes nowhere.
	 */
	applicantMessageTargets: OasisApplicationStatus[];
	/**
	 * Whether OARM may clear this application into component review. Served separately
	 * because clearance has its own endpoint — it materializes the review lanes — so it
	 * is not a plain status transition, but it IS offered in the same control.
	 */
	canClear: boolean;
	/** Lane statuses the caller may move each lane to, keyed by `componentCode`. */
	componentTransitions: Record<string, ComponentReviewStatus[]>;
}

export interface OasisApplicationView {
	header: OasisApplicationHeader;
	body: IOasisApplicationBase;
	componentReviews: ComponentReview[];
	events: WorkflowEvent[];
	flags: {
		canEdit: boolean;
		canResubmit: boolean;
		canWithdraw: boolean;
		locked: boolean;
		/**
		 * Applicant may start the Honors Program → SLIP Law Clerk transfer. Applicant
		 * read only. Derived server-side from the transfer window, the current program,
		 * whether one was already started, and whether the application can still
		 * re-open — do NOT re-derive any of that here.
		 */
		canRequestProgramChange?: boolean;
		/**
		 * Applicant may undo their own withdrawal, restoring the status they held before
		 * it. Applicant read only. Derived server-side from the write window AND from
		 * finding a prior status in the event history — neither of which the client can
		 * evaluate, so do NOT re-derive it from the status alone.
		 */
		canReopen?: boolean;
	};
	/** Staff read only; the applicant's capability is expressed as `flags` above. */
	actions?: OasisAvailableActions;
}

// ─── Request / list shapes ───────────────────────────────────────────────────

/** Page returned by `GET /oasis-application`. */
export interface OasisApplicationListPage {
	items: OasisApplicationHeader[];
	cursor: string | null;
}

/**
 * One requested file within an upload batch.
 *
 * `docType` is required for OASIS: the Supporting Documents submit gate keys off
 * the transcript category, so an unclassified upload would be invisible to it.
 * (The shared platform upload standard treats `docType` as optional; OASIS
 * narrows that.)
 */
export interface UploadItemInput {
	docType: OasisDocType;
	filename: string;
	contentType: string;
	sizeBytes: number;
}

/**
 * Body for `POST /oasis-application/{id}/uploads`.
 *
 * The platform upload contract is a batch — a single-file upload is a
 * one-element `documents` array. The applicant uploader sends one file per
 * request so each file keeps its own retry, but the wire shape is the same.
 */
export interface UploadRequestInput {
	documents: UploadItemInput[];
}

/**
 * Payload of `POST /oasis-application/{id}/uploads`: one presign per requested
 * file, in request order, each echoing its `index`.
 */
export interface PresignUploadResponse {
	documents: PresignedPutResponse[];
}

/** Error object carried whenever `success` is false. */
export interface OasisApiError {
	code: string;
	message: string;
	issues?: { index: number; code: string; message: string }[];
	timestamp?: string;
}

/**
 * Standard response envelope returned by every OASIS-external endpoint.
 * Mirrors the shared api-response-standard: branch on `success` (not the HTTP
 * status), `status` is a semantic string label, and `error` is populated iff
 * `success` is false. A not-found read is `success: true` with `payload: null`.
 */
export interface OasisApiResponse<T> {
	status: string;
	success: boolean;
	payload: T;
	error: OasisApiError | null;
}
